import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    MenuItem
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getFunnelOpportunity, createFunnelOpportunity, updateFunnelOpportunity } from "../../services/funnelOpportunities";
import { getFunnelCards } from "../../services/funnelCards";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    paper: {
        borderRadius: 12,
        boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25) !important",
        border: "1px solid rgba(0, 0, 0, 0.05)"
    }
}));

const OpportunitySchema = Yup.object().shape({
    title: Yup.string()
        .min(3, "Título muito curto")
        .required("Obrigatório"),
    funnelCardId: Yup.number().required("Obrigatório"),
});

const OpportunityModal = ({ open, onClose, funnelId, opportunityId, reload, onCardChanged }) => {
    const classes = useStyles();
    const [cards, setCards] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [products, setProducts] = useState([]);

    const initialState = {
        title: "",
        description: "",
        funnelCardId: "",
        contactId: "",
        value: "",
        productIds: [],
    };

    const [data, setData] = useState(initialState);

    useEffect(() => {
        const fetchData = async () => {
            if (!funnelId) return;

            try {
                // Buscar cards do funil
                const { data: cardsData } = await getFunnelCards(funnelId);
                setCards(cardsData);

                // Buscar contatos
                const { data: contactsData } = await api.get("/contacts");
                setContacts(contactsData.contacts || []);

                // Buscar produtos
                const { data: productsData } = await api.get("/products");
                setProducts(productsData || []);

                // Se for edição, buscar oportunidade
                if (opportunityId) {
                    const { data: oppData } = await getFunnelOpportunity(opportunityId);
                    setData({
                        title: oppData.title || "",
                        description: oppData.description || "",
                        funnelCardId: oppData.funnelCardId || "",
                        contactId: oppData.contactId || "",
                        value: oppData.value || "",
                        productIds: Array.isArray(oppData.productIds) ? oppData.productIds : []
                    });
                } else {
                    setData(initialState);
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchData();
    }, [funnelId, opportunityId, open]);

    const handleClose = () => {
        setData(initialState);
        onClose();
    };

    const handleSave = async (values) => {
        try {
            const payload = {
                ...values,
                funnelId: parseInt(funnelId),
                funnelCardId: parseInt(values.funnelCardId),
                contactId: values.contactId ? parseInt(values.contactId) : null,
                value: values.value ? parseFloat(values.value) : null,
            };

            // Check if card changed (only for edit mode)
            const cardChanged = opportunityId && data.funnelCardId && data.funnelCardId !== values.funnelCardId;
            console.log("[OpportunityModal] Card change check:", {
                opportunityId,
                oldCardId: data.funnelCardId,
                newCardId: values.funnelCardId,
                cardChanged
            });

            if (opportunityId) {
                await updateFunnelOpportunity(opportunityId, payload);
            } else {
                await createFunnelOpportunity(payload);
            }
            toast.success(i18n.t("kanban.opportunities.toasts.success"));
            if (reload) reload();

            // If card changed, notify parent to open temperature modal
            if (cardChanged && onCardChanged) {
                const oldCard = cards.find(c => c.id === data.funnelCardId);
                const newCard = cards.find(c => c.id === values.funnelCardId);
                console.log("[OpportunityModal] Calling onCardChanged with:", { oldCard, newCard });
                onCardChanged({
                    opportunityId,
                    title: values.title,
                    fromCard: oldCard,
                    toCard: newCard,
                    funnelCardId: values.funnelCardId
                });
            }
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
                <DialogTitle>
                    {opportunityId
                        ? i18n.t("kanban.opportunities.modal.edit")
                        : i18n.t("kanban.opportunities.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={data}
                    enableReinitialize={true}
                    validationSchema={OpportunitySchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSave(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.opportunities.modal.form.title")}
                                    name="title"
                                    error={touched.title && Boolean(errors.title)}
                                    helperText={touched.title && errors.title}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.opportunities.modal.form.description")}
                                    name="description"
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    select
                                    label={i18n.t("kanban.opportunities.modal.form.card")}
                                    name="funnelCardId"
                                    error={touched.funnelCardId && Boolean(errors.funnelCardId)}
                                    helperText={touched.funnelCardId && errors.funnelCardId}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                >
                                    {cards.map((card) => (
                                        <MenuItem key={card.id} value={card.id}>
                                            {card.name}
                                        </MenuItem>
                                    ))}
                                </Field>
                                <Field
                                    as={TextField}
                                    select
                                    label={i18n.t("kanban.opportunities.modal.form.contact")}
                                    name="contactId"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {contacts.map((contact) => (
                                        <MenuItem key={contact.id} value={contact.id}>
                                            {contact.name}
                                        </MenuItem>
                                    ))}
                                </Field>
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.opportunities.modal.form.value")}
                                    name="value"
                                    type="number"
                                    inputProps={{ step: "0.01" }}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    select
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {selected.map((value) => {
                                                    const product = products.find(p => p.id === value);
                                                    return product ? (
                                                        <span key={value} style={{
                                                            padding: '2px 8px',
                                                            backgroundColor: '#e0e0e0',
                                                            borderRadius: 12,
                                                            fontSize: '0.85rem'
                                                        }}>
                                                            {product.name}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        )
                                    }}
                                    label={i18n.t("kanban.opportunities.modal.form.products")}
                                    name="productIds"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                >
                                    {products.map((product) => (
                                        <MenuItem key={product.id} value={product.id}>
                                            {product.name}
                                        </MenuItem>
                                    ))}
                                </Field>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {opportunityId
                                        ? i18n.t("global.buttons.save")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default OpportunityModal;
